import { Injectable } from '@angular/core';
import { ApiClient } from '../api.client';
import {
  RetailProduct,
  RetailProductStockLevel
} from '../../models/retail-product';
import { SalesHistoryItem } from '../../models/sale-history-item';
import { PaginatedResults } from '../../models/PaginatedResults';

@Injectable()
export class RetailProductService {
  private resourceEndpoint = 'retail-products';

  constructor(private http: ApiClient) {}

  public get(id: number) {
    return this.http.get<RetailProduct>(`${this.resourceEndpoint}/${id}`);
  }

  public getAll() {
    return this.http.get<RetailProduct[]>(this.resourceEndpoint);
  }

  public getRetailProduct(id: number, include: string[] = []) {
    return this.http.get<RetailProduct>(
      `${this.resourceEndpoint}/${id}${this.http.getWiths(include)}`
    );
  }

  public addRetailProduct(product: Partial<RetailProduct>) {
    return this.http.post<RetailProduct>(`${this.resourceEndpoint}`, product);
  }

  public updateRetailProduct(id: number, product: Partial<RetailProduct>) {
    return this.http.put<RetailProduct>(
      `${this.resourceEndpoint}/${id}`,
      product
    );
  }

  public checkCode(type: 'code' | 'barcode' | 'both', value: string) {
    return this.http.get<RetailProduct>(
      `${this.resourceEndpoint}/check/${type}/${value}`
    );
  }

  search(query: string) {
    return this.http.get<RetailProduct[]>(
      `${this.resourceEndpoint}/search/${query}`
    );
  }

  getPagedSaleHistory(productId: number, pageIndex: number, pageSize: number) {
    return this.http.get<PaginatedResults<SalesHistoryItem>>(
      `${this.resourceEndpoint}/${productId}/sale-history?page=${pageIndex}&pageSize=${pageSize}`
    );
  }

  getStockLevels(productId: number) {
    return this.http.get<RetailProductStockLevel[]>(
      `${this.resourceEndpoint}/${productId}/stock-levels`
    );
  }

  getForCategory(categoryId: number[]) {
    return this.http.get<RetailProductCategory[]>(
      `${this.resourceEndpoint}/by-category?category_ids[]=${categoryId.join(`&category_ids[]=`)}&max_depth=1`
    );
  }

  createStockLevel(
    productId: number,
    payload: {
      location_id: number;
      quantity: number;
      reorder_level: number;
      reorder_quantity: number;
    }
  ) {
    return this.http.post<RetailProductStockLevel>(
      `${this.resourceEndpoint}/${productId}/stock-levels`,
      payload
    );
  }

  updateStockLevel(
    productId: number,
    stockLevelId: number,
    payload: {
      location_id: number;
      quantity: number;
      reorder_level: number;
      reorder_quantity: number;
    }
  ) {
    return this.http.put<RetailProductStockLevel>(
      `${this.resourceEndpoint}/${productId}/stock-levels/${stockLevelId}`,
      payload
    );
  }
}

interface RetailProductCategory {
  id: number;
  name: string;
  order: number;
  children: RetailProductCategory[];
  color: string | null;
  products: {
    id: number;
    name: string;
    weight: number;
    description: string;
    imageSrcSet: string;
    image: string;
    code: string;
    barcode: string;
    slug: string;
  }[];
}
